import FrameSampler from '../frameSampler'
import { colourShares, averageColour } from '../colourDetector'
import { rgbToHex } from '../colorUtils'

// Live colour readout of the frame, refreshed every tick by the detector and
// read by the ColorOptions sidebar: the composition bars plus the mean colour
// (with hex) so a user can sample a colour here and paste it into a rule. Not
// persisted — it only reflects what the camera sees right now.
export const colorShareState = {
    shares: [],
    averageRgb: [0, 0, 0],
    averageHex: '#000000',
    // Default: average the whole screen. Turn `aim` on to sample only the
    // draggable box (aimBox, normalized 0-1) so you can point at one object.
    aim: false,
    aimBox: { x: 0.4, y: 0.4, w: 0.2, h: 0.2 },
}

// A pixel's share must clear this to be worth showing as its own bar.
const MIN_SHARE = 0.02

export default function createColorDetector() {
    let sampler = null

    return {
        id: 'color',
        label: 'Color Detection',
        accent: '#ec4899',

        async load() {
            if (!sampler) sampler = new FrameSampler()
        },

        async detect({ video }) {
            if (!sampler) return null
            const frame = sampler.sample(video)
            const shares = colourShares(frame, sampler.width, sampler.height, 2)
            // Average the whole frame by default, or just the aim box when the
            // user has turned Aim on (so the background can't wash out a colour).
            const region = colorShareState.aim ? colorShareState.aimBox : null
            const avg = averageColour(
                frame,
                sampler.width,
                sampler.height,
                1,
                region
            )
            colorShareState.shares = shares
            colorShareState.averageRgb = avg
            colorShareState.averageHex = rgbToHex(avg)

            const shown = shares.filter((s) => s.share >= MIN_SHARE)
            return {
                // Draw the aim box (with its sampled colour) only in aim mode.
                boxes: colorShareState.aim
                    ? [
                          {
                              ...colorShareState.aimBox,
                              label: 'Aim',
                              accent: colorShareState.averageHex,
                          },
                      ]
                    : [],
                title: 'Colour Composition',
                insights: [
                    { label: 'Average', value: colorShareState.averageHex },
                    ...shown.slice(0, 5).map((s) => ({
                        label: s.name,
                        value: `${Math.round(s.share * 100)}%`,
                    })),
                ],
            }
        },

        reset() {
            colorShareState.shares = []
            colorShareState.averageRgb = [0, 0, 0]
            colorShareState.averageHex = '#000000'
        },
        dispose() {
            sampler = null
            colorShareState.shares = []
            colorShareState.averageRgb = [0, 0, 0]
            colorShareState.averageHex = '#000000'
        },
    }
}
